/* eslint-disable indent */
/*
 Flights Manager

 Created on      : 23-05-2014
 Author          : Manuel Diez

 This widget is the cornerstone of the whole application. It is in charge of composing the searching object that will later be sent to the
 Backend to perform a search. Uses a Backbone Model which is the brain of the widget, and dictates how the rest behaves accordingly. Using
 the carefully crafted validation function, the Model knows about its own correctness, and in certain scenarios can fire specific events
 that trigger peculiar behaviors.

 The process of initialization goes like this:
 - The model is initialized with some dumb and invalid values
 - The view is initialized, all UIElements are enhanced and references to the contained widgets are stored in the view.widgets array.
 This is also the point in which all the events of the DOM and cached widgets should be bound to its respective handlers.
 - On the controller initialization, the change event of the model is monitored, and whenever it becomes valid or invalid, this is
 what dictates if the search object is correct and the search button should be enabled.

 Once initialized, the user can start interacting with it. All widgets and UIElements included in this widget should fire a 'change' event
 when their value change into a valid state, and an 'invalid' event whenever they jump into an invalid value. Two handlers ('changeHandler'
 and 'invalidHandler') take care of updating the model according to what has changed, and that's it, the rest of behavior happens through
 events fired on the model that are received either in this widget or in the handlers.

 General guidelines (that should apply to all widgets):
 - If a specific behavior of this widget affects ONLY ONE flow, it should be managed in the handler of said flow, using events. This
 is true for progressive disclosure, for example.
 - If a specific behavior affects MORE THAN ONE, but NOT ALL the flows, it should be enabled or disabled using widget options that
 may come from the Backend (JSP or JSON options). Whenever possible, the least amount possible of 'if' statements should be used.
 For example, if something should happen when an element triggers certain event, the 'if' should be placed on the binding statement.
 - If a specific behavior affects all the flows, naturally it's part of the widget.

 */

    'use strict';
    const Flights = require('../apps/app.flights');
    var validator = new (require('../modules/validator'))();
    var trackingUtils = require('../modules/utils/tracking.utils.js');
    var tripTypeUtils = require('../modules/utils/trip_type');
    var mpiRoutes = require('../modules/enums/mpi_routes.enum');
    var $adultsSpinner;
    var dynpackValidSearchWasDone = false;
    var MAX_CHILDREN = 6;
    var MAX_INFANTS = 4;

    Odigeo.defineWidget('flightsmanager', Odigeo.Widget.extend({
        modelClass: Odigeo.Model.extend({
            defaults: {
                airlinesCodes: [],
                cabinClass: 'TOURIST',
                checkInDate: null,
                checkOutDate: null,
                collectionMethod: '',
                directFlightsOnly: false,
                dynpackCheckbox: false,
                flexibleDates: null,
                numAdults: 1,
                numberOfRooms: 1,
                numChildren: 0,
                numGuests: 0,
                numInfants: 0,
                rooms: {
                    roomAdults0: 2,
                    roomKids0: 0,
                    room0Kid0Age: 2,
                    room0Kid1Age: 2,
                    roomInfants0: 0,
                    room0Infant0Age: 0,
                    room0Infant1Age: 0,
                    roomAdults1: 2,
                    roomKids1: 0,
                    room1Kid0Age: 2,
                    room1Kid1Age: 2,
                    roomInfants1: 0,
                    room1Infant0Age: 0,
                    room1Infant1Age: 0,
                    roomAdults2: 2,
                    roomKids2: 0,
                    room2Kid0Age: 2,
                    room2Kid1Age: 2,
                    roomInfants2: 0,
                    room2Infant0Age: 0,
                    room2Infant1Age: 0,
                    roomAdults3: 2,
                    roomKids3: 0,
                    room3Kid0Age: 2,
                    room3Kid1Age: 2,
                    roomInfants3: 0,
                    room3Infant0Age: 0,
                    room3Infant1Age: 0,
                },
                searchSegments: [],
                mpiRoutes: mpiRoutes,
            },

            // Store fields that have been modified
            haveBeenModified: {},

            init: function() {},

            /*
             * Validates all fields, returns something useful to identify in which widget it has failed
             */
            validate: function(attrs) {
                var type = attrs.type;
                var segments = attrs.searchSegments;
                var errors = [];

                errors = errors.concat(validator.validateSegmentsLength(segments));

                if (Odigeo._.isEmpty(errors)) {
                    errors = errors.concat(validator.validateFlighType(type));
                    errors = errors.concat(validator.validateSegmentsLengthAndFlightType(segments, type));
                    errors = errors.concat(validator.validateSegmentsContent(segments, type));
                    errors = errors.concat(validator.validateCabinClass(attrs.cabinClass));
                    errors = errors.concat(validator.validateCalendarIncomplete(segments));
                    errors = errors.concat(validator.validateCheckinDates(attrs.checkInDate, attrs.checkOutDate));
                }

                if (!Odigeo._.isEmpty(errors)) {
                    return errors;
                }
            },

            setFlightType: function(flightType) {
                var segments = this.get('searchSegments');
                var currentType = this.get('type');
                var trip;

                if (flightType === currentType) {
                    return;
                }

                switch (flightType) {
                    case 'oneWayTrip':
                        segments = segments.slice(0, 1);
                        while (segments.length < 1) {
                            segments.push({});
                        }
                        break;

                    case 'roundTrip':
                        // In roundtrip, there must be two segments, the second being the reversed version of the first
                        segments = segments.slice(0, 1);

                        while (segments.length < 2) {
                            segments.push({});
                        }
                        if (!Odigeo._.isEmpty(segments[0])) {
                            trip = segments[0];
                            if (Odigeo._.has(trip, 'departure')) {
                                segments[1].destination = trip.departure;
                            }
                            if (Odigeo._.has(trip, 'destination')) {
                                segments[1].departure = trip.destination;
                            }
                        }

                        break;

                    case 'multipleTrip':
                        segments = segments.slice(0, 1);
                        while (segments.length < 2) {
                            segments.push({});
                        }
                        break;
                }

                this.set({
                    type: flightType,
                    searchSegments: segments,
                });
            },

            addSegment: function() {
                var segments = Odigeo._.clone(this.get('searchSegments'));

                segments.push({});
                this.set('searchSegments', segments);

                return segments.length;
            },

            removeSegment: function() {
                var segments = Odigeo._.initial(this.get('searchSegments'));

                this.set('searchSegments', segments);

                return segments.length;
            },

            updateNumberOfRooms: function(dif) {
                var numberOfRooms = this.get('numberOfRooms');

                numberOfRooms += dif;
                this.set('numberOfRooms', numberOfRooms);

                return numberOfRooms;
            },

            getLength: function() {
                return this.get('searchSegments').length;
            },

            setSegment: function(idx, value) {
                var segments = Odigeo._.clone(this.get('searchSegments'));
                var type = this.get('type');

                if (idx > segments.length) {
                    return;
                }

                segments[idx] = Odigeo._.extend({}, segments[idx], value);
                Odigeo._.forEach(validator.validateCalendarIncomplete(segments), Odigeo._.bind(function(element) {
                    this.widget.view.$cachedSelectors.datepickers[element.index].clear();
                }, this));

                // Special for RoundTrip
                if (type === 'roundTrip' && idx === 0) {
                    if (Odigeo._.has(value, 'departure')) {
                        segments[1] = Odigeo._.extend({}, segments[1], {
                            destination: value.departure,
                        });
                    }
                    if (Odigeo._.has(value, 'destination')) {
                        segments[0] = Odigeo._.extend({}, segments[0], {
                            destination: value.destination,
                        });
                        segments[1] = Odigeo._.extend({}, segments[1], {
                            departure: value.destination,
                        });
                    }
                }
                this.set('searchSegments', segments);
            },

            getRequest: function() {
                // The model keeps the date in Date format for easier manipulation, but the Backend will need them in certain format
                var json = this.toJSON();
                var segments = json.searchSegments;

                json.searchSegments = Odigeo._.map(segments, function(seg) {
                    return Odigeo._.extend({}, seg, {
                        dateStr: Odigeo.Utils.formatDate(seg.dateStr, 'isoDate', true),
                    });
                });

                if (Odigeo._.has(json, 'checkInDate') && !(Odigeo._.isNull(json.checkInDate) || Odigeo._.isUndefined(json.checkInDate))) {
                    json.checkInDate = Odigeo.Utils.formatDate(json.checkInDate, 'isoDate', true);
                    json.checkOutDate = Odigeo.Utils.formatDate(json.checkOutDate, 'isoDate', true);
                }

                if (this.widget.hasSelectedInspirationalDestination()) {
                    json.isInspirationalSearch = true;
                }

                return json;
            },
        }),

        viewClass: Odigeo.View.extend({

            templateSettings: {
                evaluate: /\{\{#([\s\S]+?)\}\}/g,
                interpolate: /\{\{[^#{]([\s\S]+?)[^}]\}\}/g,
                escape: /\{\{\{([\s\S]+?)\}\}\}/g,
            },

            init: function() {
                if (this.widget.options.activeTab) {
                    Odigeo.Session.set('lastActiveTab', this.widget.options.activeTab);
                }
                this.cacheDomSelectors();
                this.cacheWidgets();
                this.initWidgets();
                this.bindWidgets();
                this.setFlightType('roundTrip');
                Odigeo._.bindAll(this, 'updateMDDeparture');
                this.updatePassengers();
                this.clearRoom(0, 2);
                if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                    this.updateAges('kid', 'kids', MAX_CHILDREN);
                    this.updateAges('infant', 'infants', MAX_INFANTS);
                }
            },

            selectors: {
                addRoom: '.add_room',
                calendarContainers: '.calendar_container',
                citySearchWrapper: '.city_search_wrapper',
                deleteRoom: '.delete_room',
                directFlightOnlyCheckbox: '#direct-flight-switcher',
                flexibleDates: '.flexible_dates',
                flexibleSwitcher: '#flexible-dates-switcher',
                flightClassDisplay: '#class_selector',
                flightClassSelector: '#flight-class-switcher',
                flightsSeparator: '.flight_separator_wrapper',
                flightsSeparatorMobile: '.flights_separator_mobile',
                groupsMD: '.multipleTripGroup',
                groupsMDAddFlight: '.add_flight',
                groupsMDAddFlightWrapper: '.add_flight_wrapper',
                groupsMDDeleteFlight: '.delete_flight',
                groupsMDWrapper: '.multipleTripGroupsWrapper',
                kidAgeSelector: '.kid_age_selector',
                infantAgeSelector: '.infant_age_selector',
                mobileFlightInputBlocks: '.flight_input_block',
                moreOptionsCheckbox: '#moreoptions_checkbox',
                moreOptionsDropdownTitleClass: '.moreoptions_title_class',
                moreOptionsDropdownTitleStops: '.moreoptions_title_stops',
                paxSelectorLabel: '.paxselector_label',
                paxSelectorWrapper: '.pax_selector_wrapper',
                roomAgeSelector: '.room_age_selector',
                roomSelector: '.room_selector',
                searchButton: '.search_flight_button',
                searchDPButton: '.search_flight_hotel_button',
                swapOriginDestinationWrapper: '.swap_origin_destination',
                swapOriginDestinationIcon: '.swap_origin_destination_icon',
                airlineFilter: '.flightsManager_airline_filter',
                banner: '.flightsManager_banner',
                dynpackButtonContainer: '.dynpack_button_container',
            },

            events: {
                'change @kidAgeSelector': 'onKidAgeSelector',
                'change @infantAgeSelector': 'onInfantAgeSelector',
                'change @moreOptionsCheckbox': 'moreOptionsHandler',
                'change @roomAgeSelector': 'onRoomAgeSelector',
                'change #airline-filter-checkbox': 'onAirlineSelector',
                'tap .adult_quick_access': 'onQuickPassengersAccess',
                'tap .city_selection_link': 'onCitySelectionTap',
                'tap .od-moreoptions-label': 'onMoreOptionsTap',
                'tap .passengers_selection_link': 'onPassengersSelectionTap',
                'tap @addRoom': 'onAddRoom',
                'tap @deleteRoom': 'onDeleteRoom',
                'tap @groupsMDAddFlight': 'onAddFlight',
                'tap @groupsMDDeleteFlight': 'onDeleteFlight',
                'tap @searchButton': 'onSearchButton',
                'tap @searchDPButton': 'onSearchDPButton',
                'change @directFlightOnlyCheckbox': 'onDirectFlightCheckbox',
                'tap @swapOriginDestinationWrapper': 'onSwapOriginDestination',
            },

            // Stores the currently focused widget
            focusedWidget: null,

            // These are used to identify what kind of widget from their widgetID
            regexp: {
                calendar: /^calendar(\d+)$/,
                checkAccommodationDates: /^check(In|Out)Date$/,
                collectionMethod: /^flightsManager-collectionMethodId/,
                departure: /^departure-autocomplete(\d+)$/,
                destination: /^destination-autocomplete(\d+)$/,
                directFlight: /^direct-flight-switcher$/,
                dynpackCheckbox: /^dynpackCheckbox$/,
                flexibleDates: /^flexible-dates-switcher$/,
                flightClass: /^flight-class-switcher/,
                flightType: /^flight-type-switcher$/,
                passengers: /^(adults|kids|infants)$/,
                resident: /^resident-discount$/,
                roomPassengers: /^(roomAdults(\d+)|roomKids(\d+)|roomInfants(\d+))$/,
                rowGroupChildren: /^(departure-autocomplete(\d+)|destination-autocomplete(\d+)|flight-type-switcher)$/,
            },

            savedRTDate: null,

            cacheDomSelectors: function() {
                var allCalendars = $();

                for (var i = 0; i < this.widget.options.multipleTripLength; i++) {
                    var $calendar = $('#calendar' + i);
                    this.addSelectorToCache('calendar' + i, $calendar);
                    allCalendars = allCalendars.add($calendar);
                }
                allCalendars = allCalendars.add(this.$widget.find('#checkInDate')).add(this.$widget.find('#checkOutDate'));

                this.addSelectorToCache('calendars', allCalendars);

                this.addSelectorToCache('checkInDatesGroupWrapper', this.$widget.find('.checkInDatesGroupWrapper'));
            },

            cacheWidgets: function() {
                var widgets = {
                    checkDates: $('#checkdates-check'),
                    checkInDate: $('#checkInDate'),
                    checkOutDate: $('#checkOutDate'),
                    collectionMethod: $('#flightsManager-collectionMethodId'),
                    directFlight: $('#direct-flight-switcher'),
                    dynpackCheckbox: $('#dynpackCheckbox'),
                    flexibleDates: $('#flexible-dates-switcher'),
                    flightClass: $('#flight-class-switcher'),
                    flightType: $('#flight-type-switcher'),
                    numAdults: $('#adults'),
                    numChildren: $('#kids'),
                    numInfants: $('#infants'),
                    resident: $('#resident-discount'),
                    roomAges: {},
                    rooms: {},
                    segments: [],
                };
                var $calendar;
                var fillSegments = function(i) {
                    $calendar = this.$cachedSelectors['calendar' + i];
                    Odigeo.requireWidgets(
                        'departure-autocomplete' + i, 'airport_selector',
                        'destination-autocomplete' + i, 'airport_selector',
                        function(departure, destination) {
                            widgets.segments.push({
                                date: $calendar,
                                departure: departure,
                                destination: destination,
                            });
                        });
                    return ($calendar && widgets.segments[i].departure && widgets.segments[i].destination);
                };

                // Collect segments
                _(Odigeo._.range(this.widget.options.multipleTripLength)).every(fillSegments, this);

                // Collect room selectors
                for (var i = 0; i < 4; i++) {
                    widgets.rooms['roomAdults' + i] = $('#roomAdults' + i);
                    widgets.rooms['roomKids' + i] = $('#roomKids' + i);
                    widgets.rooms['roomInfants' + i] = $('#roomInfants' + i);
                    widgets.roomAges['room' + i + 'FirstKidAge'] = $('#room' + i + 'FirstKidAge');
                    widgets.roomAges['room' + i + 'SecondKidAge'] = $('#room' + i + 'SecondKidAge');
                    widgets.roomAges['room' + i + 'FirstInfantAge'] = $('#room' + i + 'FirstInfantAge');
                    widgets.roomAges['room' + i + 'SecondInfantAge'] = $('#room' + i + 'SecondInfantAge');
                }
                if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                    this.addAgesToWidget(MAX_CHILDREN, this.model.get('kids'), 'kid');
                    this.addAgesToWidget(MAX_INFANTS, this.model.get('infants'), 'infant');
                }
                this.widgets = widgets;
            },
            addAgesToWidget: function(maxPeople, modelAttribute, elementType) {
                Odigeo._.bindAll(Odigeo._.each(Odigeo._.range(maxPeople), function(i) {
                    modelAttribute[elementType + i + 'Age'] = this.$cachedSelectors[elementType + 'AgeSelector'].find('#' + elementType + i + 'Age');
                }, this));
            },
            initFlightTypeSwitcher: function($flightType) {
                var options = $flightType.children('option');
                var hasFlightTypeSelected = !Odigeo._.isEmpty(Odigeo._.filter(options, function(option) {
                    return option.selected;
                }));
                if (!hasFlightTypeSelected) {
                    Odigeo._.first(options).selected = true;
                }
                Odigeo.UIElements.enhanceSwitcher(this.widgets.flightType);
            },

            initWidgets: function() {
                var widget = this.widget;
                this.initFlightTypeSwitcher(this.widgets.flightType);
                Odigeo.UIElements.enhanceSwitcher(this.widgets.flightClass);
                Odigeo.UIElements.enhanceCheckboxRadio(this.widgets.directFlight);
                Odigeo.UIElements.enhanceCheckboxRadio(this.widgets.dynpackCheckbox);
                Odigeo.UIElements.enhanceCheckboxRadio(this.widgets.checkDates);
                Odigeo.UIElements.enhanceCheckboxRadio(this.widgets.flexibleDates);
                $adultsSpinner = Odigeo.UIElements.enhanceSpinner(this.widgets.numAdults);
                Odigeo.UIElements.enhanceSpinner(this.widgets.numChildren);
                Odigeo.UIElements.enhanceSpinner(this.widgets.numInfants);

                if (widget.options.isProductAviable) {
                    Odigeo._.each(this.widgets.rooms, function(val) {
                        Odigeo.UIElements.enhanceSpinner(val);
                    });

                    Odigeo._.each(this.widgets.roomAges, function(val) {
                        Odigeo.UIElements.enhanceSwitcher(val);
                    });
                }
                if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                    Odigeo._.each(this.$cachedSelectors.kidAgeSelector, function(val) {
                        Odigeo.UIElements.enhanceSwitcher($(val));
                    });

                    Odigeo._.each(this.$cachedSelectors.infantAgeSelector, function(val) {
                        Odigeo.UIElements.enhanceSwitcher($(val));
                    });
                }

                this.configDatepickerSpecialOptions();
                var options = {};
                Odigeo.UIElements.enhanceDatepicker(this.widgets.checkInDate, options);
                Odigeo.UIElements.enhanceDatepicker(this.widgets.checkOutDate, options);

                if (widget.options.isExpanded) {
                    this.focusHandler(widget.id);
                }
            },

            bindWidgets: function() {
                var widgets = Odigeo._.toArray(Odigeo._.omit(this.widgets, ['segments', 'rooms', 'roomAges', 'flightClass', 'kids', 'infants']));
                var bindElement = function(el) {
                    var widgetId;

                    if (el instanceof $) {
                        widgetId = el[0].id;
                    } else {
                        widgetId = el.id;
                    }

                    // Bind their events
                    el.on('change', Odigeo._.bind(this.changeHandler, this, widgetId))
                        .on('invalid', Odigeo._.bind(this.invalidHandler, this, widgetId))
                        .on('focus', Odigeo._.bind(this.focusHandler, this, widgetId))
                        .on('blur', Odigeo._.bind(this.blurHandler, this, widgetId));
                };
                var bindAgeSelector = function(el) {
                    $(el).on(Odigeo.Events.change, function() {
                        var $select = $(this);
                        var method = $select.find('option:selected').text();
                        var $closestContainer = $select.closest('.age_selector_container');
                        var isUpdatingLabel =
                            !Odigeo._.isEmpty(method) && ((Odigeo._.size(arguments) > 1 && arguments[1] !== true) || Odigeo._.size(arguments) <= 1);
                        var labelTxt = isUpdatingLabel ? ': ' + method : '';

                        $closestContainer.find('.age_selector_visible_value').text(labelTxt);
                        $closestContainer.find('.age_selector_visible_text').toggleClass('placeholder', !isUpdatingLabel);
                    }).trigger('change', true);
                };

                widgets = widgets.concat(
                    Odigeo._.chain(this.widgets.segments)
                        .map(Odigeo._.toArray)
                        .flatten()
                        .value());

                Odigeo._.each(widgets, function(widget) {
                    if (widget === false || (widget instanceof $ && !widget.length)) {
                        return;
                    }
                    Odigeo._.bind(bindElement, this)(widget);
                }, this);

                // Bind flight class widget
                Odigeo._.bind(bindElement, this)(this.widgets.flightClass);

                if (this.widget.options.isProductAviable) {
                    Odigeo._.each(this.widgets.rooms, bindElement, this);
                    Odigeo._.each(this.widgets.roomAges, bindAgeSelector, this);
                }
                if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                    Odigeo._.each(this.model.kidAgeSelector, bindAgeSelector, this);
                    Odigeo._.each(this.model.infantAgeSelector, bindAgeSelector, this);
                }
            },

            onRoomAgeSelector: function(e) {
                var index = Number($(e.currentTarget).prop('id').split('room')[1][0]);
                this.updateRoomAgesModel(index);
            },

            onKidAgeSelector: function(e) {
                this.updateKidsAges(e);
            },

            onInfantAgeSelector: function(e) {
                this.updateInfantsAges(e);
            },
            onAddRoom: function() {
                this.blurAll();
                this.addRoom();
                return false;
            },

            onDeleteRoom: function(e) {
                var id = $(e.currentTarget).closest('.room_selector_wrapper').find('.roomselector_title').prop('id').split('_')[0];
                this.blurAll();
                this.removeRoom(Number(id.substr(id.length - 1)));
                return false;
            },

            onAirlineSelector: function(e) {
                this.widget.updateAirline($(e.currentTarget).is(':checked'));
            },

            onAddFlight: function(e) {
                if (!$(e.currentTarget).is('[disabled]')) {
                    this.blurAll();
                    this.addLeg();

                    // Analytics: event tracking
                    trackingUtils.trackEvent('searcher_flights', ({
                        'multipleTrip': 'multiple_destinations_add_leg',
                        'oneWayTrip': 'one_way_add_flight_' + this.model.getLength(),
                    }[this.model.get('type')]), Odigeo.History.getPageNameFromFragment());
                }
                return false;
            },

            onDeleteFlight: function(e) {
                var leg = $(e.currentTarget).data('index');
                this.blurAll();
                this.removeLeg(leg);

                if (this.model.get('searchSegments').length === 1) {
                    this.model.set('type', 'oneWayTrip');
                }

                // Analytics: event tracking
                trackingUtils.trackEvent('searcher_flights', ({
                    'multipleTrip': 'multiple_destinations_remove_leg',
                    'oneWayTrip': 'one_way_remove_flight_' + (leg + 1),
                }[this.model.get('type')]), Odigeo.History.getPageNameFromFragment());

                return false;
            },

            onSearchButton: function(e) {
                // For the autocomplete (alias JTP Fix) not to execute after search button tap, a defer is needed.
                e.stopPropagation();
                e.preventDefault();
                Odigeo._.defer(Odigeo._.bind(this.searchButtonHandler, this));
            },

            searchButtonHandler: function() {
                var destinationWidget = this.widgets.segments[0].destination;
                this.changeSearchButtonsState(true);
                var fromOneWayToMultipleTrip = false;

                if (this.model.get('type') === 'oneWayTrip' && this.model.get('searchSegments').length > 1) {
                    this.model.set({
                        type: 'multipleTrip',
                    });
                    fromOneWayToMultipleTrip = true;
                }

                if (destinationWidget.execute('isAnywhereLocation')) {
                    destinationWidget.execute('revalidate');
                    this.blurAll();
                }

                this.hideSubmitErrors();

                Odigeo.trigger('notification:closeHeaderDialogs');

                if (Odigeo.AB.isOfferChildrenAgeDesktop) {
                    var flightValidationResponse = this.validateFlightAges();
                    var undefinedMessage = Odigeo._.isEmpty(flightValidationResponse);
                    if (!undefinedMessage) {
                        return this.showPaxAgeSelectorError(undefinedMessage, flightValidationResponse);
                    }
                }
                if (!Odigeo._.isEmpty(this.model.validationError)) {
                    return this.checkErrors();
                }

                this.blurAll(this.$cachedSelectors.calendars);

                var request = this.model.getRequest();
                var searchData = Odigeo.Utils.parseJson(Odigeo.Storage.get('search-data'));
                if (request && searchData) {
                    var searches = {
                        newSearch: request,
                        oldSearch: searchData[0],
                    };
                    Odigeo.trigger(this.widget.id + ':trackChangesInRequest', searches);
                }

                request = Odigeo._.extend(request, {
                    internalSearch: true,
                    isInspirationalSearch: this.widget.hasSelectedInspirationalDestination(),
                    isNewTab: !!this.widget.options.isNewTab,
                });

                var buyPath = 'FLIGHTS_' + (Odigeo.History.getPageNameFromFragment() === 'flights/edit' ? 'RESULTS' : 'HOME') + '_SEARCH_FORM';
                request = Odigeo._.extend(request, {
                    buyPath: buyPath,
                });

                request = Odigeo._.omit(request, 'flexibleDates');

                // Analytics: event tracking
                trackingUtils.trackEvent('searcher_flights', 'search_flights_1');
                trackingUtils.trackEvent('searcher_flights', ({
                    'oneWayTrip': 'one_way',
                    'roundTrip': 'return',
                    'multipleTrip': 'multiple_destinations' + (fromOneWayToMultipleTrip ? '_upgrade' : ''),
                }[this.model.get('type')]), Odigeo.History.getPageNameFromFragment());

                trackingUtils.trackEventWithCategory('home', 'searcher_flights', 'cabin_class_' + this.model.get('cabinClass').toLowerCase());

                // Tells the handler to make a search
                var searchSegments = this.model.get('searchSegments');
                var firstSegment = Odigeo._.first(searchSegments);
                var departureIATA = !Odigeo._.isUndefined(firstSegment) ? firstSegment.departure.iata : '';
                var destinationIATA = !Odigeo._.isUndefined(firstSegment) ? firstSegment.destination.iata : '';

                if (Odigeo.AB.isMPIRouteDesktop && this.isMPIRoute(departureIATA, destinationIATA)) {
                    this.setMPISessionValues(departureIATA, destinationIATA);
                }
                Odigeo.trigger(this.widget.id + ':search', request);
            },
            showPaxAgeSelectorError: function(undefinedMessage, flightValidationResponse) {
                var $moreOptions = this.$el.find('.more_options');
                var $paxSelectorLabel = $moreOptions.find('.paxSelector_validation_error_message');

                $paxSelectorLabel.toggleClass('odf-hidden', undefinedMessage).html(flightValidationResponse);
                this.$el.find('.pax_selector_wrapper').toggleClass('error', !undefinedMessage);
                this.changeSearchButtonsState(false);
                return false;
            },
            isMPIRoute: function(departureIATA, destinationIATA) {
                return this.existsDepartureAndDestination(departureIATA, destinationIATA)
                    && this.existsSegmentInMPIList(departureIATA, destinationIATA);
            },
            setMPISessionValues: function(departureIATA, destinationIATA) {
                Odigeo.Storage.set('mpiRouteStartSearch', Date.now());
                Odigeo.Storage.set('departureIATA', departureIATA);
                Odigeo.Storage.set('destinationIATA', destinationIATA);
            },
            existsDepartureAndDestination: function(departureIATA, destinationIATA) {
                return !Odigeo._.isEmpty(departureIATA) && !Odigeo._.isEmpty(destinationIATA);
            },
            existsSegmentInMPIList: function(departureIATA, destinationIATA) {
                var segment = departureIATA + '-' + destinationIATA;
                return Odigeo._.contains(this.model.get('mpiRoutes'), segment);
            },
            onSearchDPButton: function(e) {
                e.stopPropagation();
                e.preventDefault();
                var category = Odigeo.SessionScope.entryPoint === 'flights' ? 'flights_tab' : 'home';

                this.hideSubmitErrors();

                Odigeo.trigger('notification:closeHeaderDialogs');

                var flightValidationResponse = this.validateFlightAges();
                var undefinedMessage = Odigeo._.isEmpty(flightValidationResponse);
                if (Odigeo.AB.isOfferChildrenAgeDesktop && !undefinedMessage) {
                    return this.showPaxAgeSelectorError(undefinedMessage, flightValidationResponse);
                }

                if (!Odigeo._.isEmpty(this.model.validationError)) {
                    trackingUtils.trackEventWithCategory(category, 'searcher_flights', 'search_dynpack_FE_error');
                    return this.checkErrors();
                }

                trackingUtils.trackEventWithCategory(category, 'searcher_flights', 'search_dynpack');
                Odigeo.trigger(this.widget.id + ':dpSearch', {searchParams: this.model.getRequest()});
            },

            onDirectFlightCheckbox: function() {
                var isChecked = this.$cachedSelectors.directFlightOnlyCheckbox.is(':checked');
                var trackingLabel = 'direct_' + (isChecked ? 'checked' : 'unchecked');
                trackingUtils.trackEventWithCategory('home', 'searcher_flights', trackingLabel);
            },

            checkErrors: function() {
                this.changeSearchButtonsState(false);

                Odigeo.trigger(this.widget.id + ':invalidSearchForm', this.model.validationError);

                // Show validation errors and send error details to Google Analytics
                Odigeo._.each(this.model.validationError, function(error) {
                    var label;

                    this.showSubmitError(error.key, error.index);

                    switch (error.key) {
                        case 'departure':
                            label = 'location_departure_leg_' + (error.index + 1) + '_error';
                            break;
                        case 'destination':
                            label = 'location_arrival_leg_' + (error.index + 1) + '_error';
                            break;
                        case 'date':
                            switch (this.model.get('type')) {
                                case 'oneWayTrip':
                                    label = 'date_departure_leg_1_error';
                                    break;
                                case 'roundTrip':
                                    label = ({0: 'date_departure_leg_1_error', 1: 'date_arrival_leg_1_error'}[error.index]);
                                    break;
                                case 'multipleTrip':
                                    label = 'date_departure_leg_' + (error.index + 1) + '_error';
                                    break;
                            }
                            break;
                    }
                    // Analytics: event tracking
                    trackingUtils.trackEvent('searcher_flights', label, Odigeo.History.getPageNameFromFragment());
                }, this);

                // Analytics: event tracking
                trackingUtils.trackEvent('searcher_flights', 'search_flights_FE_error', Odigeo.History.getPageNameFromFragment());
            },

            onFlightClassChange: function() {
                var $option = this.$cachedSelectors.flightClassSelector.find('option:selected');
                this.$cachedSelectors.flightClassDisplay.text($option.text());
                Odigeo.trigger(this.widget.id + ':passengersChanged', '');
            },

            // Dumb function, only for Google Analytics MoreOptions event tracking
            moreOptionsHandler: function() {
                if (this.$cachedSelectors.moreOptionsCheckbox.is(':checked')) {
                    trackingUtils.trackEvent('searcher_flights', 'open_more_options', Odigeo.History.getPageNameFromFragment());
                }
            },

            // Focuses (CSS way) the row in which the widget is
            focusHandler: function(widgetId) {
                this.blurAll(widgetId);
                this.focusedWidget = widgetId;
                Odigeo.trigger(this.widget.id + ':focus', widgetId);
            },

            // Blurs (CSS way) the row in which the widget is
            blurHandler: function(widgetId) {
                if (widgetId === this.focusedWidget) {
                    this.focusedWidget = null;
                }
                Odigeo.trigger(this.widget.id + ':blur', widgetId);
            },

            // Blurs the focused widget, unless it's the one being focused at the moment, passed by parameter
            blurAll: function(focused) {
                var widgetId = this.focusedWidget;
                var widgets = this.widgets;
                var r = this.regexp;
                var matches = null;
                var idx;
                var dic;

                if (!Odigeo._.isNull(widgetId) && widgetId !== focused) {
                    // Find out what has changed
                    if ((matches = widgetId.match(r.departure))) {
                        idx = Number(matches[1]);
                        widgets.segments[idx].departure.execute('blur');
                    } else if ((matches = widgetId.match(r.destination))) {
                        idx = Number(matches[1]);
                        widgets.segments[idx].destination.execute('blur');
                    } else if ((matches = widgetId.match(r.flightType))) {
                        widgets.flightType.trigger('blur');
                    } else if ((matches = widgetId.match(r.passengers))) {
                        dic = {
                            adults: 'numAdults',
                            kids: 'numChildren',
                            kidsAges: 'kids',
                            infantsAges: 'infants',
                            infants: 'numInfants',
                        };

                        widgets[dic[matches[1]]].trigger('blur');
                    } else if ((matches = widgetId.match(r.roomPassengers))) {
                        widgets.rooms[[matches[1]]].trigger('blur');
                    }
                }
            },

            changeHandler: function(widgetId) {
                var args = Odigeo._.rest(arguments).reverse();
                args.unshift(widgetId);

                this.setWidget.apply(this, args);

                Odigeo.trigger(this.widget.id + ':change', widgetId);
            },

            invalidHandler: function(widgetId) {
                if (!this.regexp.calendar.test(widgetId)) {
                    this.setWidget(widgetId, null);
                }
            },

            // Finds out what widget has changed, and updates the model accordingly
            setWidget: function(widgetId, data) {
                var r = this.regexp;
                var matches = null;
                var changed = {};
                var isPassengers = false;
                var isRoomPassengers = false;
                var index;
                var dic;

                // Find out what has changed
                if ((matches = widgetId.match(r.calendar))) {
                    var date;
                    index = Number(matches[1]);
                    this.updateCalendars(widgetId, data);
                    if (index in data.dates) {
                        date = data.dates[index];
                        this.model.setSegment.call(this.model, index, {
                            dateStr: date,
                        });
                    }
                    this.updateCheckInOutCalendars();
                } else if ((matches = widgetId.match(r.departure))) {
                    index = Number(matches[1]);
                    this.model.setSegment.call(this.model, index, {
                        departure: data || undefined,
                    });
                    this.checkSwapOriginDestinationStatus(index);
                } else if ((matches = widgetId.match(r.destination))) {
                    index = Number(matches[1]);
                    this.model.setSegment.call(this.model, index, {
                        destination: data || undefined,
                    });
                    this.checkSwapOriginDestinationStatus(index);
                } else if ((matches = widgetId.match(r.flightType))) {
                    changed.type = data || undefined;
                    this.setFlightType(changed.type);
                    this.updateCheckInOutCalendars();
                } else if ((matches = widgetId.match(r.flightClass))) {
                    changed.cabinClass = Odigeo._.isString(data) ? data : data.target.value;
                    this.widget.model.haveBeenModified.cabinClass = true;
                    if (!data.isTrigger) {
                        trackingUtils.trackEventWithCategory('home', 'searcher_flights',
                            'select_cabin_class_' + changed.cabinClass.toLowerCase());
                    }
                    this.onFlightClassChange();
                } else if ((matches = widgetId.match(r.directFlight))) {
                    changed.directFlightsOnly = data.target.checked;
                    this.widget.model.haveBeenModified.directFlightsOnly = true;
                } else if ((matches = widgetId.match(r.dynpackCheckbox))) {
                    changed.dynpackCheckbox = data.target.checked;
                    this.widget.model.haveBeenModified.dynpackCheckbox = true;
                } else if ((matches = widgetId.match(r.flexibleDates))) {
                    changed.flexibleDates = data.target.checked;
                    this.widget.model.haveBeenModified.flexibleDates = true;
                } else if ((matches = widgetId.match(r.resident))) {
                    changed.resident = data.target.checked;
                } else if ((matches = widgetId.match(r.passengers))) {
                    dic = {
                        adults: 'numAdults',
                        kids: 'numChildren',
                        infants: 'numInfants',
                    };
                    changed[dic[matches[1]]] = Odigeo._.isNull(data) ? undefined : data;
                    isPassengers = true;
                } else if ((matches = widgetId.match(r.roomPassengers))) {
                    changed.rooms = this.model.get('rooms');
                    changed.rooms[matches[1]] = Odigeo._.isNull(data) ? undefined : data;
                    isRoomPassengers = true;
                } else if ((matches = widgetId.match(r.checkAccommodationDates))) {
                    changed[matches[0]] = Odigeo._.isNull(data) || !Odigeo._.isDate(data.selectedDate) ? undefined : data.selectedDate;
                } else if ((matches = widgetId.match(r.collectionMethod))) {
                    changed.collectionMethod = $(data.target).val();
                }

                if (!Odigeo._.isEmpty(changed)) {
                    this.model.set.call(this.model, changed);

                    if (isPassengers) {
                        this.updatePassengers();
                    } else if (isRoomPassengers) {
                        this.updateRoomPassengers(widgetId.substr(widgetId.length - 1));
                    }
                }

                if (this.widget.options.isDpHomeButtonAnimation) {
                    this.checkDpButtonAnimation();
                }
            },

            checkDpButtonAnimation: function() {
                var searchSegments = this.model.get('searchSegments');
                var returnDate = this.widget.getFlightType() === 'roundTrip' ? searchSegments[1].dateStr : '';
                var numOfDays = Odigeo.Utils.getDifferenceInDays(searchSegments[0].dateStr, returnDate);

                if (this.isActiveTab('dp') || Odigeo.Utils.parseJson(Odigeo.Session.get('dpButtonAnimationWasDone'))) {
                    this.undoDpButtonAnimation();
                } else {
                    this.doDpButtonAnimation(numOfDays);
                }
            },

            isActiveTab: function(tab) {
                return this.$widget.find('.flightsManager_tab[data-value="' + tab + '"]').hasClass('active');
            },

            checkIfShallAppearDpButton: function(numOfDays) {
                if (!Odigeo._.isNaN(numOfDays) && Odigeo._.inRange(numOfDays, 31)) {
                    dynpackValidSearchWasDone = true;
                }

                return dynpackValidSearchWasDone;
            },

            undoDpButtonAnimation: function() {
                this.$cachedSelectors.searchDPButton.prop('disabled', false);
                this.$cachedSelectors.dynpackButtonContainer.removeClass('isFlightsTab active');
            },

            doDpButtonAnimation: function(numOfDays) {
                this.$cachedSelectors.searchDPButton.prop('disabled', !this.checkIfShallAppearDpButton(numOfDays));
                this.$cachedSelectors.dynpackButtonContainer.addClass('isFlightsTab');
                this.$cachedSelectors.dynpackButtonContainer.toggleClass('active', this.checkIfShallAppearDpButton(numOfDays));
                Odigeo.Session.set('dpButtonAnimationWasDone', this.$cachedSelectors.dynpackButtonContainer.hasClass('active'));
            },

            /*
             * Whenever a calendar changes, their dates need to be propagated to the other calendars.
             * This function takes care of that.
             */
            updateCalendars: function(widgetId, data) {
                this.hideSubmitError('date', data.index);
                var $calendars = this.$cachedSelectors.calendars.not(this.$cachedSelectors[widgetId]);
                // Notify all calendars of new set of dates
                if (data.modified) {
                    Odigeo._.each($calendars, function(calendar) {
                        var id = !Odigeo._.isNull(calendar.id.match(this.regexp.calendar)) ? Number(calendar.id.match(this.regexp.calendar)[1]) : -1;
                        if (Odigeo._.includes(data.modified, id)) {
                            $(calendar).trigger('config', {
                                dates: data.dates,
                                refresh: true,
                            });
                        }
                    }, this);
                } else {
                    $calendars.trigger('config', {dates: data.dates});
                }
            },

            getDateFromSegment: function(segment) {
                return !Odigeo._.isEmpty(segment) && Odigeo._.has(segment, 'dateStr') && Odigeo._.isDate(segment.dateStr) ? segment.dateStr : null;
            },

            updateCheckInOutCalendars: function() {
                var data = {
                    isPossible: false,
                };
                var searchSegments = this.model.get('searchSegments');
                var departureDate;
                var destinationDate;
                if (this.model.get('type') === 'roundTrip' && searchSegments.length > 1) {
                    departureDate = this.getDateFromSegment(searchSegments[0]);
                    destinationDate = this.getDateFromSegment(searchSegments[1]);
                    if (Odigeo._.isDate(departureDate) && Odigeo._.isDate(destinationDate) &&
                            Odigeo.Utils.getDifferenceInDays(departureDate.getTime(), destinationDate.getTime()) > 1) {
                        Odigeo._.extend(data, {
                            isPossible: true,
                            departure: departureDate,
                            destination: destinationDate,
                        });
                    }
                }
                if (!data.isPossible) {
                    this.model.set('checkInDate', undefined);
                    this.model.set('checkOutDate', undefined);
                }
                Odigeo.trigger(this.widget.id + ':checkInOutDatesStatus', data);
            },

            // Upon passenger selector changes, restricts their siblings to keep the amount of passengers between legal values.
            updatePassengers: function() {
                var model = this.model.toJSON();
                var options = this.widget.options;
                var adults = model.numAdults;
                var children = model.numChildren;
                var infants = model.numInfants;
                var maxAdults;
                var maxChildren;
                var maxInfants;
                var minAdults;
                var validationMessage = '';
                var messageData = $('#pax-selector').find('.od-ui-paxSelector-info-txt').data();

                // Set maxs and mins
                maxAdults = options.maxPassengers - children - infants;
                maxChildren = Math.min(options.maxPassengers - adults - infants,
                    adults * options.maxChildrenPerAdult - infants);
                maxInfants = Math.min(options.maxPassengers - adults - children,
                    adults * options.maxChildrenPerAdult - children,
                    adults * options.maxInfantsPerAdult);

                minAdults = Math.max(Math.round((children + infants) / options.maxChildrenPerAdult),
                    infants);

                this.widgets.numAdults.trigger('config', {
                    maxRange: maxAdults,
                    minRange: minAdults,
                });
                this.widgets.numChildren.trigger('config', {
                    maxRange: maxChildren,
                });
                this.widgets.numInfants.trigger('config', {
                    maxRange: maxInfants,
                });

                // Inform about boundaries
                if (!Odigeo._.isEmpty(messageData)) {
                    if ((adults + children + infants) === options.maxPassengers) {
                        validationMessage = messageData.infoMaxPassengers;
                    } else if (adults * options.maxChildrenPerAdult === children) {
                        validationMessage = messageData.infoMaxKids;
                    } else if (adults * options.maxInfantsPerAdult === infants) {
                        validationMessage = messageData.infoMaxChildren;
                    }
                }

                Odigeo.trigger(this.widget.id + ':passengersChanged', validationMessage);

                // Return validity
                return ((adults + children + infants) <= options.maxPassengers &&
                    children <= adults * options.maxChildrenPerAdult &&
                    infants <= adults * options.maxInfantsPerAdult);
            },

            configSpinners: function(maxAdults, maxChildren, maxInfants, minAdults, widgetIndex) {
                this.widgets.rooms['roomAdults' + widgetIndex].trigger('config', {
                    maxRange: maxAdults,
                    minRange: minAdults,
                });
                this.widgets.rooms['roomKids' + widgetIndex].trigger('config', {
                    maxRange: maxChildren,
                });
                this.widgets.rooms['roomInfants' + widgetIndex].trigger('config', {
                    maxRange: maxInfants,
                });
            },

            configDatepickerSpecialOptions: function() {
                var widget = this.widget;
                var datePickerEspecialOptions = [
                    {'specialOptions': []},
                    {'specialOptions': []},
                ];
                var allDatePickers = [];

                if (widget.options.isChangeFlightTravelInCalendar) {
                    var templateScript = '.datepicker_extra_message';
                    datePickerEspecialOptions[0]['specialOptions'].push(
                        this.configDatePikerAddOn(templateScript, {isDeparture: true}, '.od-ui-calendar-wrapper', 'prepend')
                    );
                    datePickerEspecialOptions[1]['specialOptions'].push(
                        this.configDatePikerAddOn(templateScript, {isDeparture: false}, '.od-ui-calendar-wrapper', 'prepend')
                    );
                    this.$widget.find(templateScript).remove();
                }

                Odigeo._.each(this.widgets.segments, function(segment, idx) {
                    var datePicker = Odigeo.UIElements.enhanceDatepicker($(segment.date), {
                        'swipe': false,
                    });
                    if (datePickerEspecialOptions[idx]) {
                        datePicker.addSpecialOption(datePickerEspecialOptions[idx].specialOptions);
                    }
                    allDatePickers.push(datePicker);
                }, this);
                this.addSelectorToCache('datepickers', allDatePickers);
                this.addSelectorToCache('flexibleDatesDatePickerCheckbox', $('#flexible-dates-calendar-switcher'));
            },

            // Upon room passenger selector changes, restricts their siblings to keep the amount of passengers between legal values.
            updateRoomPassengers: function(widgetIndex) {
                var options = this.widget.options;
                var adults = this.model.get('rooms')['roomAdults' + widgetIndex];
                var children = this.model.get('rooms')['roomKids' + widgetIndex];
                var infants = this.model.get('rooms')['roomInfants' + widgetIndex];
                var roomTotalGuests = adults + children + infants;
                var maxAdults;
                var maxChildren;
                var maxInfants;
                var minAdults;
                var validationMessage = '';
                var messageData = $('#room-selector').find('.ui_roomSelector_info span').data();
                var closestParent = this.widgets.rooms['roomAdults' + widgetIndex].closest('.od-moreoptions-content');
                var remainingPaxGaps;

                this.model.set('numGuests', this.model.get('numGuests') + roomTotalGuests - closestParent.data('current-guests'));
                closestParent.data('current-guests', roomTotalGuests);

                // Set maxs and mins
                remainingPaxGaps = options.maxPassengers - this.model.get('numGuests');
                remainingPaxGaps = roomTotalGuests === options.maxPaxPerRoom ? 0 : remainingPaxGaps;
                maxAdults = Math.min(remainingPaxGaps + adults, options.maxAdultsPerRoom, options.maxPaxPerRoom);
                maxChildren = Math.min(remainingPaxGaps + children, options.maxKidsAndInfantsPerRoom - infants);
                maxInfants = Math.min(remainingPaxGaps + infants,
                    options.maxKidsAndInfantsPerRoom - children,
                    adults * options.maxInfantsPerAdult);

                minAdults = Math.max(Math.round((children + infants) / options.maxChildrenPerAdult),
                    infants);

                this.configSpinners(maxAdults, maxChildren, maxInfants, minAdults, widgetIndex);

                // Inform about boundaries
                if (!Odigeo._.isEmpty(messageData)) {
                    if (this.model.get('numGuests') === 9) {
                        validationMessage = messageData.infoMaxPassengers;
                    } else if (adults === options.maxAdultsPerRoom) {
                        validationMessage = messageData.infoMaxAdults;
                    } else if (options.maxKidsAndInfantsPerRoom === children) {
                        validationMessage = messageData.infoMaxKids;
                    } else if (options.maxKidsAndInfantsPerRoom === infants) {
                        validationMessage = messageData.infoMaxInfants;
                    } else if (options.maxKidsAndInfantsPerRoom === children + infants) {
                        validationMessage = messageData.infoMaxKidsInfantsPerAdult;
                    } else if (adults <= 2 && adults * options.maxInfantsPerAdult === infants) {
                        validationMessage = messageData.infoMaxKidsInfantsPerAdult;
                    }
                }

                Odigeo.trigger(this.widget.id + ':roomPassengersChanged', {index: widgetIndex, message: validationMessage});

                if (this.model.get('numberOfRooms') === 4 || this.model.get('numGuests') === 9) {
                    this.$cachedSelectors.addRoom.parent().addClass('hidden');
                } else {
                    this.$cachedSelectors.addRoom.parent().removeClass('hidden');
                }

                // Return validity
                return ((adults + children + infants) <= (options.maxAdultsPerRoom + options.maxKidsAndInfantsPerRoom) &&
                    children <= adults * options.maxChildrenPerAdult &&
                    infants <= adults * options.maxInfantsPerAdult);
            },

            onModelValid: function() {
                this.$cachedSelectors.searchButton
                    .removeAttr('disabled')
                    .removeClass('disabled')
                    .addClass('active');

                Odigeo.trigger(this.widget.id + ':valid', this.model.getRequest());
            },

            onModelInvalid: function() {
                Odigeo.trigger(this.widget.id + ':invalid');
            },

            setFlightType: function(flightType) {
                var model = this.model.toJSON();
                var widgetId = this.widget.id;
                var oldState = model.type;
                var $sel = this.$cachedSelectors;
                var destination;

                if (!!oldState && this.widget.options.isChangeFlightTravelInCalendar && !Odigeo._.isUndefined($sel['datepickers'])) {
                    $sel['datepickers'][0].hideSpecialOption('.datepicker_one_way');
                    $sel['datepickers'][1].hideSpecialOption('.datepicker_one_way');
                }

                if (flightType === oldState) {
                    return;
                }

                // Move calendars to corresponding tab
                $sel.calendarContainers.each(function(idx, calendarContainer) {
                    var $calendarContainer = $(calendarContainer);
                    var leg = $calendarContainer.data('leg');
                    var destination = $sel.calendarContainers.filter('[data-leg="' + leg + '"][data-show="' + flightType + '"]')[0];
                    var isContainerNotEmpty = $calendarContainer.children().length > 0;
                    var isFlightsContainer = !Odigeo._.isUndefined(leg);

                    if (isContainerNotEmpty && isFlightsContainer && destination && (destination !== calendarContainer)) {
                        $calendarContainer.contents().appendTo(destination);
                        $(destination).find('[tabindex]').attr('tabindex', $(destination).data('tabIndex'));
                    }
                });

                this.hideSubmitErrors();

                // Before changing
                switch (oldState) {
                    case 'roundTrip':
                        $sel.calendars.trigger('config', {
                            permissible: false,
                        });
                        // Save Round Trip date
                        if (!Odigeo._.isEmpty(model.searchSegments[1])) {
                            this.savedRTDate = model.searchSegments[1].dateStr;
                        }
                        break;

                    case 'oneWayTrip':
                        // Hide first flight number title
                        $sel.flightsSeparator.filter('[data-title="1"]')
                            .addClass('odf-hidden');
                        $sel.flightsSeparatorMobile.removeClass('odf-hidden');
                        // Hide legs
                        $sel.groupsMD
                            .addClass('hidden')
                            .removeClass('newFlightGroup');
                        // Clear Multiple Destinations legs
                        Odigeo._.each(Odigeo._.rest(this.widgets.segments), function(segment) {
                            segment.date.trigger('clear');
                            segment.departure.execute('clear');
                            segment.destination.execute('clear');
                        });
                        break;

                    case 'multipleTrip':
                        // Hide legs
                        $sel.groupsMD
                            .addClass('hidden')
                            .removeClass('newFlightGroup');

                        // Clear Multiple Destinations legs
                        Odigeo._.each(Odigeo._.rest(this.widgets.segments), function(segment) {
                            segment.date.trigger('clear');
                            segment.departure.execute('clear');
                            segment.destination.execute('clear');
                        });

                        // Unbind event
                        this.model.off('change:searchSegments', this.updateMDDeparture);
                        break;
                }

                // Restoring the Round Trip date before notifying the model
                // If this is not present, there are glitches in tablet due to progressive disclosure.
                if (flightType === 'roundTrip') {
                    if (Odigeo._.isDate(this.savedRTDate) &&
                        model.searchSegments[0].dateStr <= this.savedRTDate) {
                        $sel.calendar1.trigger('config', {
                            value: this.savedRTDate,
                        });
                    }

                    this.savedRTDate = null;
                }

                // add trip type into dates page, it's necessary for styling, it depends on trip type
                this.$widget.attr('data-trip-type', flightType);

                this.model.setFlightType.call(this.model, flightType);

                // Capture this (including refreshed model) in handler and do flow-specific tasks
                Odigeo.trigger(widgetId + ':flightTypeChange', {
                    newState: flightType,
                    oldState: oldState,
                    model: this.model.toJSON(),
                });

                // After changing
                switch (flightType) {
                    case 'roundTrip':
                        $sel.calendars
                            .trigger('config', {
                                type: 'dual',
                                permissible: true,
                            });

                        $sel.calendar1
                            .trigger('config', {
                                placeholder: $sel.calendar1.attr('data-placeholder'),
                            });
                        break;

                    case 'oneWayTrip':
                        $sel.calendars
                            .trigger('config', {
                                type: 'single',
                            });

                        $sel.flexibleDates.removeClass('invisible');
                        $sel.groupsMDDeleteFlight.removeClass('hidden');
                        $sel.flightsSeparator.filter('[data-index]').removeClass('odf-hidden');

                        if (!$sel.groupsMDAddFlightWrapper.data('multi-cities')) {
                            $sel.groupsMDAddFlightWrapper
                                .removeClass('odf-hidden')
                                .filter('.od-flightsManager-add-flight-last')
                                .removeClass('od-flightsManager-add-flight-last');
                        }

                        $sel.flightsSeparatorMobile.filter('[data-index="0"]').addClass('odf-hidden');

                        $sel.calendar1
                            .trigger('config', {
                                placeholder: $sel.calendar0.attr('data-placeholder'),
                            })
                            .trigger('clear', true);
                        break;

                    case 'multipleTrip':
                        $sel.calendars
                            .trigger('config', {
                                type: 'multiple',
                            });

                        // Show first Multiple Destinations leg
                        $sel.groupsMD.first()
                            .removeClass('hidden')
                            .addClass('newFlightGroup');

                        // Propagate change in 1st destination to 2nd departure
                        // Only in case 1st destination is not the inspirational
                        this.model.on('change:searchSegments', this.updateMDDeparture);
                        destination = this.widgets.segments[0].destination.execute('getValue');
                        if (Odigeo._.has(destination, 'iata') && destination.iata === 'INSP' && destination.geoNodeId === 0) {
                            this.widgets.segments[0].destination.execute('clear');
                        } else {
                            this.widgets.segments[1].departure.execute('setValue', destination, false, this.widget.id + ':flightTypeChange');
                        }

                        // Change calendar placeholder
                        $sel.calendar1
                            .trigger('config', {
                                placeholder: $sel.calendar0.attr('data-placeholder'),
                            })
                            .trigger('clear');
                        break;
                }
            },

            /*
             * This function should only be binded in MultiDestination trips.
             * Checks if a destination changes, and if the next departure is empty, updates its value too.
             */
            updateMDDeparture: function(model, value) {
                var destination;
                var previous = model.previous('searchSegments') || [];
                var current = value || [];
                var prev;
                var curr;
                var next;

                // Check if it's virgin
                Odigeo._.each(Odigeo._.initial(current), function(segment, idx) {
                    prev = previous[idx] || {};
                    curr = current[idx] || {};
                    next = current[idx + 1] || {};
                    if (prev.destination !== curr.destination &&
                        Odigeo._.isEmpty(next.departure)) {
                        destination = this.widgets.segments[idx].destination.execute('getValue');
                        this.widgets.segments[idx + 1].departure.execute('setValue', destination);
                    }
                }, this);
            },

            sameCityHandler: function(idx, enable, city) {
                this.widgets.segments[idx].destination.execute('toggleSameLocation', enable, city);
            },

            /*
             * Adds a new leg in MultiDestination both in the view and the model.
             * Doesn't check whether is possible to do so according to the correctness of the previous one, so be careful.
             */
            addLeg: function() {
                var length = this.model.addSegment();
                var type = this.widgets.flightType.val();
                var lastAddButton = type === 'multipleTrip' ? length - 3 : length - 2;
                var lastDestination;

                // Show next segment
                this.$cachedSelectors.groupsMD
                    .removeClass('newFlightGroup');
                this.$cachedSelectors.groupsMD
                    .eq(length - 2)
                    .removeClass('hidden')
                    .addClass('newFlightGroup');
                // Hide "add flight" button for the current segment
                this.$cachedSelectors.groupsMDAddFlightWrapper
                    .filter('[data-show="' + type + '"]')
                    .eq(lastAddButton)
                    .addClass('odf-hidden');
                // Style "add flight" button for the next segment
                this.$cachedSelectors.groupsMDAddFlightWrapper
                    .filter('[data-show="' + type + '"]')
                    .eq(lastAddButton + 1)
                    .addClass('od-flightsManager-add-flight-last');
                // Hide separator line
                this.$cachedSelectors.flightsSeparator
                    .filter('[data-index="' + (length - 2) + '"]')
                    .addClass('odf-hidden');

                var dates = Odigeo._.map(this.widget.model.changed.searchSegments, function(val) {
                    return Odigeo._.isUndefined(val.dateStr) ? null : new Date(val.dateStr);
                });

                this.$cachedSelectors.calendars
                    .trigger('config', {
                        size: length,
                    });

                this.$cachedSelectors['calendar' + (length - 1)].trigger('config', {
                    value: {dates: dates},
                });

                // Copy previous destination value to the next segment departure value
                lastDestination = this.widgets.segments[length - 2].destination.execute('getValue') || {};
                this.widgets.segments[length - 1].departure.execute('setValue', lastDestination);

                if (length === 2) {
                    // Show flight titles for the 1st and 2nd segments
                    this.$cachedSelectors.flightsSeparator
                        .filter('[data-title="1"], [data-title="2"]')
                        .removeClass('odf-hidden');
                    this.$cachedSelectors.flightsSeparatorMobile.removeClass('odf-hidden');
                    // Hide "flexible date" option for more than 1 flight
                    this.$cachedSelectors.flexibleDates.addClass('invisible');
                }
            },

            /*
             * Removes a leg in MultiDestination.
             * If it's not the last one, updates the model to keep the others untouched, changing the indices.
             */
            removeLeg: function(index) {
                var length = this.model.getLength();
                var widgets = this.widgets.segments;
                var type = this.widgets.flightType.val();
                var lastAddButton = type === 'multipleTrip' ? length - 3 : length - 2;
                var legs = this.model.get('searchSegments');
                var i;

                // Copy values from next leg
                for (i = index; i < length - 1; i++) {
                    widgets[i].departure.execute('setValue', widgets[i + 1].departure.execute('getValue'));
                    widgets[i].destination.execute('setValue', widgets[i + 1].destination.execute('getValue'));
                    widgets[i].date.trigger('config', {value: legs[i + 1].dateStr});
                    widgets[i + 1].date.trigger('clear');
                    widgets[i + 1].departure.execute('clear');
                    widgets[i + 1].destination.execute('clear');
                }

                // Clear if last
                if (index === length - 1) {
                    widgets[index].date.trigger('clear', true);
                    widgets[index].departure.execute('clear');
                    widgets[index].destination.execute('clear');
                }

                length = this.model.removeSegment();

                this.$cachedSelectors.calendars
                    .trigger('config', {
                        size: length,
                    });

                // Hide current segment
                this.$cachedSelectors.groupsMD
                    .eq(length - 1)
                    .removeClass('newFlightGroup')
                    .addClass('hidden');
                this.$cachedSelectors.groupsMD
                    .eq(length - 2)
                    .addClass('newFlightGroup');
                // Show "add flight" button for the current segment
                this.$cachedSelectors.groupsMDAddFlightWrapper
                    .filter('[data-show="' + type + '"]')
                    .eq(lastAddButton)
                    .removeClass('odf-hidden');
                if (length >= 2) {
                    // Style "add flight" button for the last segment
                    this.$cachedSelectors.groupsMDAddFlightWrapper
                        .filter('[data-show="' + type + '"]')
                        .eq(lastAddButton)
                        .addClass('od-flightsManager-add-flight-last');
                }
                // Show separator line
                this.$cachedSelectors.flightsSeparator
                    .filter('[data-index="' + (length - 1) + '"]')
                    .removeClass('odf-hidden');

                if (length === 1) {
                    // Hide flight titles for the 1st and 2nd segments
                    this.$cachedSelectors.flightsSeparator
                        .filter('[data-title="1"], [data-title="2"]')
                        .addClass('odf-hidden');
                    this.$cachedSelectors.flightsSeparatorMobile
                        .filter('[data-index="0"], [data-index="1"]')
                        .addClass('odf-hidden');
                    // Show "flexible date" option for more than 1 flight
                    this.$cachedSelectors.flexibleDates.removeClass('invisible');
                }
            },

            /*
             * Adds a new room in Flight+Hotel both in the view and the model.
             */
            addRoom: function() {
                var numberOfRooms = this.model.updateNumberOfRooms(1);
                var numAdults = this.model.get('numGuests') === (this.widget.options.maxPassengers - 1) ? 1 : 2;

                this.clearRoom(numberOfRooms - 1, numAdults);
                $(this.$cachedSelectors.roomSelector.find('.room_selector_wrapper.hidden')[0]).removeClass('hidden');
                if (numberOfRooms === 4 || this.model.get('numGuests') === 9) {
                    this.$cachedSelectors.addRoom.parent().addClass('hidden');
                }
            },

            getRoomGuests: function(roomIndex) {
                return this.widgets.rooms['roomAdults' + roomIndex].closest('.od-moreoptions-content').data('current-guests');
            },

            setRoomGuests: function(roomIndex, value) {
                this.widgets.rooms['roomAdults' + roomIndex].closest('.od-moreoptions-content').data('current-guests', value);
            },

            clearRoom: function(i, numAdults) {
                var rooms = this.widgets.rooms;

                this.setRoomGuests(i, 0);
                rooms['roomKids' + i].trigger('setValue', 0);
                rooms['roomInfants' + i].trigger('setValue', 0);
                rooms['roomAdults' + i].trigger('setValue', Odigeo._.isUndefined(numAdults) ? 2 : numAdults);
                this.updateRoomAges(i, true);
            },

            /*
             * Updates room ages.
             * If reset param value is true, it's setted with default values.
             * In any other case, room ages are updated with next room's ages values.
             */
            updateRoomAges: function(index, reset) {
                var roomsModel = this.model.get('rooms');
                var roomAges = this.widgets.roomAges;

                if (reset === true) {
                    roomsModel['room' + index + 'Kid0Age'] = 2;
                    roomsModel['room' + index + 'Kid1Age'] = 2;
                    roomsModel['room' + index + 'Infant0Age'] = 0;
                    roomsModel['room' + index + 'Infant1Age'] = 0;
                    roomAges['room' + index + 'FirstKidAge'].val('');
                    roomAges['room' + index + 'SecondKidAge'].val('');
                    roomAges['room' + index + 'FirstInfantAge'].val('');
                    roomAges['room' + index + 'SecondInfantAge'].val('');
                } else {
                    roomsModel['room' + index + 'Kid0Age'] = Number(roomAges['room' + (index + 1) + 'FirstKidAge'].val());
                    roomsModel['room' + index + 'Kid1Age'] = Number(roomAges['room' + (index + 1) + 'SecondKidAge'].val());
                    roomsModel['room' + index + 'Infant0Age'] = Number(roomAges['room' + (index + 1) + 'FirstInfantAge'].val());
                    roomsModel['room' + index + 'Infant1Age'] = Number(roomAges['room' + (index + 1) + 'SecondInfantAge'].val());
                    roomAges['room' + index + 'FirstKidAge'].val(roomAges['room' + (index + 1) + 'FirstKidAge'].val());
                    roomAges['room' + index + 'SecondKidAge'].val(roomAges['room' + (index + 1) + 'SecondKidAge'].val());
                    roomAges['room' + index + 'FirstInfantAge'].val(roomAges['room' + (index + 1) + 'FirstInfantAge'].val());
                    roomAges['room' + index + 'SecondInfantAge'].val(roomAges['room' + (index + 1) + 'SecondInfantAge'].val());
                }
                this.model.set({rooms: roomsModel});
                Odigeo.trigger(this.widget.id + ':roomAgesChanged', index);
            },
            updateAges: function(elementType, modelAttribute, maxNumber) {
                var model = this.model.get(modelAttribute);

                Odigeo._.each(Odigeo._.range(maxNumber), function(i) {
                    model[elementType + i + 'Age'] = '';
                });

                this.model.set(modelAttribute, model);
                Odigeo.trigger(this.widget.id + ':' + elementType + 'AgesChanged');
            },

            updateRoomAgesModel: function(index) {
                var roomsModel = this.model.get('rooms');
                var roomAges = this.widgets.roomAges;

                roomsModel['room' + index + 'Kid0Age'] = Number(roomAges['room' + index + 'FirstKidAge'].val());
                roomsModel['room' + index + 'Kid1Age'] = Number(roomAges['room' + index + 'SecondKidAge'].val());
                roomsModel['room' + index + 'Infant0Age'] = Number(roomAges['room' + index + 'FirstInfantAge'].val());
                roomsModel['room' + index + 'Infant1Age'] = Number(roomAges['room' + index + 'SecondInfantAge'].val());

                this.model.set({rooms: roomsModel});
            },

            updateKidsAges: function(e) {
                var kidsModel = this.model.get('kids');
                kidsModel[e.currentTarget.id] = e.currentTarget.value;
                this.model.set({kids: kidsModel});
                this.$el.find('#' + e.currentTarget.id).val($(e.currentTarget).val());
                this.changeAgeSelector($(e.currentTarget));
            },

            updateInfantsAges: function(e) {
                var infantsModel = this.model.get('infants');
                infantsModel[e.currentTarget.id] = e.currentTarget.value;
                this.model.set({infants: infantsModel});
                this.$el.find('#' + e.currentTarget.id).val($(e.currentTarget).val());
                this.changeAgeSelector($(e.currentTarget));
            },

            changeAgeSelector: function($el) {
                var value = $el.val();
                var isNullValue = Odigeo._.isNull(value);
                var validationResponse = this.validateFlightAges();
                var $paxSelector = this.$el.find('#pax-selector');
                var paxSelectorInfo = $paxSelector.find('.ui_paxSelector_info');
                $el.closest('.age_selector_container').parent().removeClass('open');
                if (Odigeo._.isEmpty(validationResponse)) {
                    paxSelectorInfo.addClass('odf-hidden');
                    this.togglePaxAgeSelectorError(validationResponse);
                }
                $el.closest('.age_selector_container').parent().find('.age_selector_visible_text').toggleClass('placeholder', isNullValue);
                $el.closest('.age_selector_container').parent().find('.age_selector_visible_value').text(isNullValue ? '' : ': ' + value);
            },

            /*
             * Removes a room in Flight+Hotel.
             * If it's not the last one, updates the model to keep the others untouched, changing the indices.
             */
            removeRoom: function(index) {
                var length = this.model.get('numberOfRooms');
                var rooms = this.widgets.rooms;
                var i;

                // Copy values from next leg
                for (i = index; i < length - 1; i++) {
                    var nextItemError;
                    rooms['roomAdults' + i].trigger('setValue', rooms['roomAdults' + (i + 1)].val());
                    rooms['roomKids' + i].trigger('setValue', rooms['roomKids' + (i + 1)].val());
                    rooms['roomInfants' + i].trigger('setValue', rooms['roomInfants' + (i + 1)].val());
                    nextItemError = $('#room' + (i + 1) + '_passengers_selector').closest('.roomselector_label').parent().find('.room_error').html();
                    Odigeo.trigger(this.widget.id + ':toggleRoomSelectorError',
                        {index: i, message: Odigeo._.isEmpty(nextItemError) ? undefined : nextItemError});
                    this.updateRoomAges(i);
                    this.model.set('numGuests', this.model.get('numGuests') - this.getRoomGuests(i + 1));
                    this.setRoomGuests(i + 1, 0);
                }

                if (index === length - 1) {
                    this.model.set('numGuests', this.model.get('numGuests') - this.getRoomGuests(index));
                    this.setRoomGuests(index, 0);
                }

                this.model.updateNumberOfRooms(-1);
                this.updateSpinners();
                this.$cachedSelectors.roomSelector.find('.room_selector_wrapper:visible:last').addClass('hidden');
                this.$cachedSelectors.addRoom.parent().removeClass('hidden');
            },

            updateSpinners: function() {
                var length = this.model.get('numberOfRooms');
                var i;

                for (i = 0; i < length; i++) {
                    this.updateRoomPassengers(i);
                }
            },
            tapPaxSelectorLabel: function() {
                var $paxSelectorLabel = this.$cachedSelectors.paxSelectorLabel;
                var $paxSelectorWrapper = this.$cachedSelectors.paxSelectorWrapper;
                $paxSelectorLabel.off().on(Odigeo.Events.tap, function() {
                    var $this = $(this);
                    var $thisMoreOptions = $this.closest('.od-moreoptions');

                    $thisMoreOptions.removeClass('open');
                    $thisMoreOptions.toggleClass('open');
                    $paxSelectorWrapper.toggleClass('open');
                });

                this.$el.find('.pax_selector_wrapper .closeBtn').on(Odigeo.Events.tap, Odigeo._.bind(function(e) {
                    var validationResponse = this.validateFlightAges();
                    var $this = $(e.target);
                    if (Odigeo._.isEmpty(validationResponse)) {
                        this.showPaxAgeSelectionError(validationResponse);
                        $this.closest('.pax_selector_wrapper').removeClass('open');
                        e.stopPropagation();
                    } else {
                        this.showPaxAgeSelectionError(validationResponse);
                    }
                    this.togglePaxAgeSelectorError(validationResponse);
                }, this));
            },

            togglePaxAgeSelectorError: function(validationResponse) {
                var $paxSelectorLabelText = this.$cachedSelectors.paxSelectorLabel.find('.ui_paxSelector_info_txt');
                var undefinedMessage = Odigeo._.isEmpty(validationResponse);

                $paxSelectorLabelText.toggleClass('odf-hidden', undefinedMessage).html(validationResponse);
            },

            showPaxAgeSelectionError: function(validationResponse) {
                var $paxAgeSelector = this.$el.find('#pax-selector');
                var $paxAgeSelectorInfo = $paxAgeSelector.find('.ui_paxSelector_info');
                $paxAgeSelectorInfo.toggleClass('odf-hidden', Odigeo._.isEmpty(validationResponse));
                if (!Odigeo._.isEmpty(validationResponse)) {
                    $paxAgeSelectorInfo.find('.ui_paxSelector_info_txt').html(validationResponse).addClass('od-error-message');
                }
            },
            changeAgeSelectors: function() {
                var $flightsManagerKidAgeSelector = this.$cachedSelectors.kidAgeSelector;
                var $flightsManagerInfantAgeSelector = this.$cachedSelectors.infantAgeSelector;
                var $flightsManagerAgeSelectors = $flightsManagerKidAgeSelector.add($flightsManagerInfantAgeSelector);
                $flightsManagerAgeSelectors.on(Odigeo.Events.change, Odigeo._.bind(function(e) {
                    var $this = $(e.target);
                    var value = $this.val();
                    var isNullValue = Odigeo._.isNull(value);
                    var validationResponse = this.validateFlightAges();
                    var $paxAgeSelector = this.$el.find('#pax-selector');
                    var $paxAgeSelectorInfo = $paxAgeSelector.find('.ui_paxSelector_info');
                    var $ageSelectorContainer = $this.closest('.age_selector_container');
                    $ageSelectorContainer.parent().removeClass('open');
                    if (Odigeo._.isEmpty(validationResponse)) {
                        $paxAgeSelectorInfo.addClass('odf-hidden');
                        this.togglePaxAgeSelectorError(validationResponse);
                    }
                    $ageSelectorContainer.parent().find('.age_selector_visible_text').toggleClass('placeholder', isNullValue);
                    $ageSelectorContainer.parent().find('.age_selector_visible_value').text(isNullValue ? '' : ': ' + value);
                }, this)).on(Odigeo.Events.focus, function() {
                    $(this).closest('.age_selector_container').parent().addClass('open');
                }).on(Odigeo.Events.blur, function() {
                    $(this).closest('.age_selector_container').parent().removeClass('open');
                });
            },

            validateFlightAges: function() {
                var validationMessage;
                var $paxAgeSelector = this.$el.find('#pax-selector');
                var messageData = $paxAgeSelector.find('.ui_paxSelector_info span').data();
                var validationData = {
                    $visibleKidAges: this.$el.find('.kid_age_selector_container').not('.odf-hidden'),
                    $visibleInfantsAges: this.$el.find('.infant_age_selector_container').not('.odf-hidden'),
                    emptyKidAges: 0,
                    emptyInfantAges: 0,
                };
                // Inform about errors
                if (!Odigeo._.isEmpty(messageData)) {
                    Odigeo._.each(validationData.$visibleKidAges, function(item) {
                        var $item = $(item).find('.kid_age_selector');
                        var selectorValue = $(item).find('.age_selector_visible_value').html();
                        if (Odigeo._.isEmpty(selectorValue)) {
                            $item.closest('.kid_age_selector_container').addClass('error');
                            validationData.emptyKidAges++;
                        } else {
                            $item.closest('.kid_age_selector_container').removeClass('error');
                        }
                    }, validationData);
                    Odigeo._.each(validationData.$visibleInfantsAges, function(item) {
                        var $item = $(item).find('.infant_age_selector');
                        var selectorValue = $(item).find('.age_selector_visible_value').html();
                        if (Odigeo._.isEmpty(selectorValue)) {
                            $item.closest('.infant_age_selector_container').addClass('error');
                            validationData.emptyInfantAges++;
                        } else {
                            $item.closest('.infant_age_selector_container').removeClass('error');
                        }
                    }, validationData);
                    validationMessage = this.getValidationMessages(validationData, messageData);
                }
                return validationMessage;
            },
            getValidationMessages: function(validationData, messageData) {
                var validationMessageKid = this.hasToShowKidMessage(validationData) ? this.getValidationMessageKids(validationData, messageData) : '';
                var validationMessageInfant = this.hasToShowInfantMessage(validationData)
                    ? this.getValidationMessageInfants(validationData, messageData)
                    : '';
                return this.getCompleteValidationMessage(validationMessageKid, validationMessageInfant, messageData.infoKidInfantAge);
            },
            hasToShowKidMessage: function(validationData) {
                return validationData.emptyKidAges > 0 && validationData.$visibleKidAges.length > 0;
            },
            hasToShowInfantMessage: function(validationData) {
                return validationData.emptyInfantAges > 0 && validationData.$visibleInfantsAges.length > 0;
            },
            getCompleteValidationMessage: function(messageKid, messageInfant, messageKidInfant) {
                if (!Odigeo._.isEmpty(messageKid)) {
                    return !Odigeo._.isEmpty(messageInfant) ? messageKidInfant : messageKid;
                } else {
                    return !Odigeo._.isEmpty(messageInfant) ? messageInfant : '';
                }
            },
            getValidationMessageKids: function(validationData, messageData) {
                return validationData.$visibleKidAges.length == 1 ? messageData.infoKidAge : messageData.infoKidsAge;
            },
            getValidationMessageInfants: function(validationData, messageData) {
                return validationData.$visibleInfantsAges.length == 1 ? messageData.infoInfantAge : messageData.infoInfantsAge;
            },
            validateAges: function(widgetIndex, silent) {
                var messageData = $('#room-selector').find('.ui_roomSelector_info span').data();
                var validationMessage;
                var visibleChildrenAges;
                var visibleInfantsAges;
                var emptyChildrenAges = 0;
                var emptyInfantsAges = 0;

                visibleChildrenAges = $($('.room_children_ages')[widgetIndex]).find('select:visible');
                visibleInfantsAges = $($('.room_infants_ages')[widgetIndex]).find('select:visible');

                // Inform about errors
                if (!Odigeo._.isEmpty(messageData)) {
                    Odigeo._.each(visibleChildrenAges, function(item) {
                        var $item = $(item);
                        if (Odigeo._.isNull($item.val())) {
                            emptyChildrenAges++;
                            if (!silent) {
                                $item.closest('.age_selector_container').parent().addClass('error');
                            }
                        } else {
                            $item.closest('.age_selector_container').parent().removeClass('error');
                        }
                    }
                    );

                    Odigeo._.each(visibleInfantsAges, function(item) {
                        var $item = $(item);
                        if (Odigeo._.isNull($item.val())) {
                            emptyInfantsAges++;
                            if (!silent) {
                                $item.closest('.age_selector_container').parent().addClass('error');
                            }
                        } else {
                            $item.closest('.age_selector_container').parent().removeClass('error');
                        }
                    }
                    );

                    if (visibleChildrenAges.length === 1 && visibleInfantsAges.length === 1) {
                        if (emptyChildrenAges > 0 && emptyInfantsAges > 0) {
                            validationMessage = messageData.infoKidInfantAge;
                        } else if (emptyChildrenAges > 0) {
                            validationMessage = messageData.infoKidAge;
                        } else if (emptyInfantsAges > 0) {
                            validationMessage = messageData.infoInfantAge;
                        }
                    } else if (visibleChildrenAges.length === 1) {
                        if (emptyChildrenAges > 0) {
                            validationMessage = messageData.infoKidAge;
                        }
                    } else if (visibleInfantsAges.length === 1) {
                        if (emptyInfantsAges > 0) {
                            validationMessage = messageData.infoInfantAge;
                        }
                    } else if (visibleChildrenAges.length === 2) {
                        if (emptyChildrenAges === 1) {
                            validationMessage = messageData.infoKidAge;
                        } else if (emptyChildrenAges === 2) {
                            validationMessage = messageData.infoKidsAge;
                        }
                    } else if (visibleInfantsAges.length === 2) {
                        if (emptyInfantsAges === 1) {
                            validationMessage = messageData.infoInfantAge;
                        } else if (emptyInfantsAges === 2) {
                            validationMessage = messageData.infoInfantsAge;
                        }
                    }
                }

                return validationMessage;
            },

            configDatePikerAddOn: function(template, options, injectionSelector, injectionPosition) {
                var specialOptionTemplate = Odigeo._.template(this.$widget.find(template).html())(options);
                var $specialOptionTemplate = $(specialOptionTemplate);

                return {
                    'injectionSelector': injectionSelector,
                    'injectionPosition': injectionPosition,
                    'dom': specialOptionTemplate,
                    'action': {
                        'selector': $specialOptionTemplate.data('actionSelector'),
                        'evt': $specialOptionTemplate.data('actionEvent'),
                        'trigger': $specialOptionTemplate.data('actionTrigger'),
                    },
                };
            },

            checkPaxQuickAccess: function(adults, kids, infants) {
                var $paxQuickAccess = this.$el.find('.pax_quick_access');
                $paxQuickAccess.find('.active').removeClass('active');
                if (adults > 3 || kids > 0 || infants > 0) {
                    $paxQuickAccess.addClass('odf-hidden');
                } else {
                    $paxQuickAccess.removeClass('odf-hidden');
                    $paxQuickAccess.find('[data-quick-adults="' + adults + '"]').addClass('active');
                }
            },

            updateAgeSelector: function() {
                this.updatePassengersSelectorContainerWidth(this.model.get('numChildren'), this.model.get('numInfants'));
                this.modifyKidsAndChildrenAgeSelectorVisibility(this.model.get('numChildren'), this.model.get('numInfants'));
            },
            modifyKidsAndChildrenAgeSelectorVisibility: function(numKids, numInfants) {
                var hideKidsAgeSelector = numKids == 0;
                var hideInfantsAgeSelector = numInfants == 0;
                var hideSecondBarKidsAgeSelector = numKids < 4;
                var hideSecondBarInfantsAgeSelector = numInfants < 4;

                this.$el.find('.pax_kid_age_container').toggleClass('odf-hidden', hideKidsAgeSelector);
                this.$el.find('.pax_infant_age_container').toggleClass('odf-hidden', hideInfantsAgeSelector);
                this.$el.find('.pax_kid_ages_second_block').toggleClass('odf-hidden', hideSecondBarKidsAgeSelector);
                this.$el.find('.pax_infant_ages_second_block').toggleClass('odf-hidden', hideSecondBarInfantsAgeSelector);
                this.showAgeContainers('.kid_age_selector_container', numKids, 'kid');
                this.showAgeContainers('.infant_age_selector_container', numInfants, 'infant');
            },
            showAgeContainers: function(containerClass, maxPeople, peopleType) {
                Odigeo._.each(this.$el.find(containerClass), function(ageSelector) {
                    $(ageSelector).toggleClass('odf-hidden', maxPeople < $(ageSelector).attr('data-'+peopleType+'-age'));
                });
            },
            updatePassengersSelectorContainerWidth: function(numKids, numInfants) {
                this.$el.find('.od-moreoptions-content')
                    .attr('data-current-kids', numKids)
                    .attr('data-current-infants', numInfants);
            },
            // Fills the form with the given data.
            fillForm: function(data) {
                var widgets = this.widgets;
                var model = this.model;
                var widget = this.widget;
                var hasSeparateStay = false;
                var checkHasSeparateStay = function(flightDateStr, accommodationDateStr) {
                    return !Odigeo._.isEmpty(accommodationDateStr) ? flightDateStr !== accommodationDateStr : false;
                };

                // Reset model
                // model.clear().set(model.defaults);
                this.savedRTDate = null;

                if (!data) {
                    return;
                }

                // Set flight type
                if (!Odigeo._.isUndefined(data.type)) {
                    widgets.flightType
                        .trigger('enable')
                        .trigger('setValue', data.type);

                    // Set number of legs
                    if (data.type === 'multipleTrip' && !Odigeo._.isUndefined(data.searchSegments)) {
                        while (model.get('searchSegments').length < data.searchSegments.length) {
                            this.addLeg();
                        }
                    }
                }

                // Set legs
                Odigeo._.each(data.searchSegments, function(seg, idx) {
                    var departure = Odigeo._.omit(seg.departure, 'fullname');
                    var destination = Odigeo._.omit(seg.destination, 'fullname');
                    var accommodationCheckingDate = idx === 0 ? data.checkInDate : data.checkOutDate;

                    // Don't fill the departure and destination in the second leg of RoundTrip
                    if (data.type !== 'roundTrip' || idx === 0) {
                        if (!Odigeo._.isEmpty(seg.departure)) {
                            widgets.segments[idx].departure
                                .execute('clear')
                                .execute('setValue', Odigeo._.extend(departure, {
                                    value: seg.departure.fullname || seg.departure.city + ' - ' + seg.departure.iata,
                                }));
                        }
                        if (!Odigeo._.isEmpty(seg.destination)) {
                            widgets.segments[idx].destination
                                .execute('clear')
                                .execute('setValue', Odigeo._.extend(destination, {
                                    value: seg.destination.fullname || seg.destination.city + ' - ' + seg.destination.iata,
                                }));
                        }
                    }

                    var date;
                    if (!Odigeo._.isEmpty(seg.dateStr)) {
                        date = Odigeo.Utils.parseDate(seg.dateStr);
                        widgets.segments[idx].date.trigger('close').trigger('config', {
                            value: date,
                        });
                        hasSeparateStay = hasSeparateStay || checkHasSeparateStay(seg.dateStr, accommodationCheckingDate);
                    }
                });

                if (data.type === 'roundTrip' && data.searchSegments.length === 1) {
                    widgets.segments[1].date.trigger('close').trigger('clear');
                }

                // Set everything else
                var extraOmitablesItems = Odigeo.AB.isOfferChildrenAgeDesktop ? [] : this.omitKidAndInfantSelectors();
                Odigeo._.each(Odigeo._.omit(data, 'searchSegments', 'type', extraOmitablesItems), function(val, key) {
                    switch (key) {
                        case 'cabinClass':
                            widgets.flightClass.trigger('setValue', val);
                            break;
                        case 'directFlightsOnly':
                            widgets.directFlight.prop('checked', val).trigger('change');
                            break;
                        case 'dynpackCheckbox':
                            widgets.dynpackCheckbox.prop('checked', val).trigger('change');
                            break;
                        case 'flexibleDates':
                            widgets.flexibleDates.prop('checked', val).trigger('change');
                            break;
                        case 'resident':
                            Odigeo.trigger(widget.id + ':changeResident', val);
                            break;
                        case 'airlinesCodes':
                            model.attributes.airlinesCodes = val;
                            break;
                        case 'numAdults':
                        case 'numChildren':
                        case 'numInfants':
                            widgets[key].trigger('setValue', val);
                            break;
                        case 'kid0Age':
                        case 'kid1Age':
                        case 'kid2Age':
                        case 'kid3Age':
                        case 'kid4Age':
                        case 'kid5Age':
                        case 'infant0Age':
                        case 'infant1Age':
                        case 'infant2Age':
                        case 'infant3Age':
                            this.$el.find('#' + key).trigger('setValue', val);
                            break;
                        case 'checkInDate':
                        case 'checkOutDate':
                            if (!Odigeo._.isEmpty(val)) {
                                model.set(key, new Date(Odigeo.Utils.parseDate(val)), {silent: true});
                            }
                            break;
                        case 'numberOfRooms':
                            model.set(key, val, {silent: true});
                            break;
                        case 'rooms':
                            this.updatePassengersWidget(val);
                            break;
                    }
                }, this);

                // Separate stay
                widgets.checkDates.prop('checked', hasSeparateStay);
                this.updateCheckInOutCalendars();

                // Tells the handler the search form has been filled
                if (data && data.searchSegments && data.searchSegments.length > 0) {
                    Odigeo.trigger(this.widget.id + ':searchFormFilled', {searchData: data});
                }
            },

            omitKidAndInfantSelectors: function() {
                var kidAndInfantItems = [];
                Odigeo._.each(Odigeo._.range(MAX_CHILDREN), function(i) {
                    kidAndInfantItems.push('kid' + i + 'Age');
                });
                Odigeo._.each(Odigeo._.range(MAX_INFANTS), function(i) {
                    kidAndInfantItems.push('infant' + i + 'Age');
                });

                return kidAndInfantItems;
            },
            updatePassengersWidget: function(roomsCfg) {
                var numberOfRooms = this.model.get('numberOfRooms');
                var roomsRange = Odigeo._.range(numberOfRooms);

                this.model.set('numberOfRooms', 1);
                this.resetNumGuests(roomsRange);
                this.resetRoomInputsVisibility(this.$cachedSelectors.roomSelector.find('.room_selector_wrapper:gt(0)'));
                this.updateRoomInputs(roomsRange, roomsCfg);
            },

            updateRoomInputs: function(roomsRange, roomsCfg) {
                var roomsWidget = this.widgets.rooms;
                var agesWidget = this.widgets.roomAges;
                var setAgeValue = function(widgetId, value) {
                    if (!Odigeo._.isUndefined(value)) {
                        agesWidget[widgetId].trigger('setValue', value);
                    }
                };

                Odigeo._.each(roomsRange, function(roomIdx) {
                    var roomCfg = roomsCfg[roomIdx];
                    if (roomIdx !== 0) {
                        this.addRoom();
                    }
                    roomsWidget['roomAdults' + roomIdx].trigger('setValue', +roomCfg.numAdults);
                    roomsWidget['roomKids' + roomIdx].trigger('setValue', +roomCfg.kidsAges.length);
                    roomsWidget['roomInfants' + roomIdx].trigger('setValue', +roomCfg.infantsAges.length);
                    setAgeValue('room' + roomIdx + 'FirstKidAge', +roomCfg.kidsAges[0]);
                    setAgeValue('room' + roomIdx + 'SecondKidAge', +roomCfg.kidsAges[1]);
                    setAgeValue('room' + roomIdx + 'FirstInfantAge', +roomCfg.infantsAges[0]);
                    setAgeValue('room' + roomIdx + 'SecondInfantAge', +roomCfg.infantsAges[1]);
                    this.updateRoomAgesModel(roomIdx);
                }, this);
            },

            resetRoomInputsVisibility: function(inputs) {
                Odigeo._.each(inputs, function(el) {
                    $(el).addClass('hidden');
                });
            },

            resetNumGuests: function(roomsRange) {
                this.model.set('numGuests', 0);
                Odigeo._.each(roomsRange, function(roomIdx) {
                    this.setRoomGuests(roomIdx, 0);
                }, this);
            },

            onCitySelectionTap: function(ev) {
                var $el = $(ev.currentTarget);
                var page = $el.attr('data-page');
                var idx = $el.attr('data-index');
                var isWebmasterPage = Odigeo.Utils.isWebmasterPage();
                Odigeo.Router.navigate('flights/' + page + idx, {preservePath: isWebmasterPage});
            },

            onPassengersSelectionTap: function(ev) {
                ev.stopPropagation();
                var $el = $(ev.currentTarget);
                var idx = $el.attr('data-index') || '';
                var isWebmasterPage = Odigeo.Utils.isWebmasterPage();

                Odigeo.Router.navigate('flights/passengers' + idx, {preservePath: isWebmasterPage});
            },

            changeSearchButtonsState: function(state) {
                this.$cachedSelectors.searchButton.attr('disabled', state || null);
                this.$cachedSelectors.searchDPButton.attr('disabled', state || null);
            },

            onMoreOptionsTap: function(ev) {
                var $el = $(ev.currentTarget);
                var $thisMoreOptions = $el.closest('.od-moreoptions');

                $thisMoreOptions.toggleClass('open');
                this.$el.find('.od-moreoptions').not($thisMoreOptions).removeClass('open');
                this.$el.find('roomselector_label').not($el).removeClass('open');

                if ($thisMoreOptions.hasClass('open')) {
                    Odigeo.trigger(this.widget.id + ':pauseCarousel');
                    if (!ev.isTrigger) {
                        Odigeo.trigger(this.widget.id + ':pauseQuotesDynamic');
                    }
                }
            },

            onQuickPassengersAccess: function(ev) {
                ev.stopPropagation();
                var $el = $(ev.currentTarget);
                var val = $el.attr('data-quick-adults');
                $adultsSpinner.setValue(ev, val);
                trackingUtils.trackEvent('searcher_flights', 'passenger_shortcut_' + val);
            },

            showAirlineFilter: function(airlineFilterName, hasToBeChecked) {
                if (this.$cachedSelectors.airlineFilter.length) {
                    var airlineFilterText = this.$cachedSelectors.airlineFilter.data('text').replace('%1$s', airlineFilterName);
                    this.$cachedSelectors.airlineFilter.find('.flightsManager-airline-text').html(airlineFilterText);
                    this.$cachedSelectors.airlineFilter.find('#airline-filter-checkbox').attr('checked', hasToBeChecked);
                    this.widget.updateAirline(hasToBeChecked);
                    this.$cachedSelectors.airlineFilter.removeClass('odf-hidden');
                    this.$cachedSelectors.banner.addClass('odf-hidden');
                }
            },

            // added for AB test OFIS-266
            hideSubmitError: function(fieldType, legIndex) {
                this._toggleSubmitError(fieldType, legIndex, true);
            },

            // added for AB test OFIS-266
            hideSubmitErrors: function() {
                this.$cachedSelectors.mobileFlightInputBlocks.find('.error_message').addClass('odf-hidden');
                this.$cachedSelectors.mobileFlightInputBlocks.find('.iata').removeClass('od-citySearch-city-error');
            },

            // added for AB test OFIS-266
            showSubmitError: function(fieldType, legIndex) {
                this._toggleSubmitError(fieldType, legIndex, false);
            },

            // added for AB test OFIS-266
            _toggleSubmitError: function(fieldType, legIndex, state) {
                switch (fieldType) {
                    case 'departure':
                    case 'destination':
                        this.$cachedSelectors.mobileFlightInputBlocks
                            .find('[data-page="' + fieldType + '"][data-index="' + legIndex + '"] .error_message')
                            .toggleClass('odf-hidden', state);
                        this.$cachedSelectors.mobileFlightInputBlocks
                            .find('[data-page="' + fieldType + '"][data-index="' + legIndex + '"] .iata')
                            .toggleClass('od-citySearch-city-error', !state);
                        break;
                    case 'date':
                        this.$cachedSelectors.mobileFlightInputBlocks
                            .find('#date_selection' + legIndex + ' .error_message')
                            .toggleClass('odf-hidden', state);
                        break;
                }
            },

            changeAddFlightWrapperVisibility: function(isVisible) {
                if (this.model.get('type') === 'oneWayTrip') {
                    this.$cachedSelectors.groupsMDAddFlightWrapper
                        .toggleClass('odf-hidden', !isVisible)
                        .data('multi-cities', !isVisible);
                }
            },

            checkSwapOriginDestinationStatus: function(idx) {
                var segments = this.model.get('searchSegments');
                var isActive = segments !== null && (!Odigeo._.isUndefined(this.model.get('searchSegments')[idx].departure) ||
                    !Odigeo._.isUndefined(this.model.get('searchSegments')[idx].destination));

                this.$cachedSelectors.swapOriginDestinationWrapper.eq(idx).toggleClass('active', isActive);
            },

            changeSwapOriginDestinationVisibility: function(isVisible) {
                this.$cachedSelectors.swapOriginDestinationWrapper.toggleClass('odf-hidden', !isVisible);
            },

            onSwapOriginDestination: function(evt) {
                var idx = parseInt($(evt.currentTarget).data('idx'), 10);
                var departure = this.widgets.segments[idx].departure;
                var destination = this.widgets.segments[idx].destination;
                var departureValue = departure.execute('getValue');
                var destinationValue = destination.execute('getValue');

                if (!Odigeo._.isNull(departureValue) || !Odigeo._.isNull(destinationValue)) {
                    this.$cachedSelectors.swapOriginDestinationIcon.eq(idx).toggleClass('rotate');
                    Odigeo.trigger(this.widget.id + ':swapOriginDestinationAnimation', {idx: idx, type: 'add'});

                    destination.execute('setValue', null);
                    departure.execute('setValue', null);
                    destination.execute('setValue', departureValue);
                    departure.execute('setValue', destinationValue);

                    setTimeout(Odigeo._.bind(function() {
                        var input = Odigeo._.isNull(departureValue) ? 'destination' : (Odigeo._.isNull(destinationValue) ? 'departure' : '');
                        Odigeo.trigger(this.widget.id + ':swapOriginDestinationAnimation', {idx: idx, type: 'remove', focus: input});
                    }, this), 400);

                    this.onSwapOriginDestinationTracking(departureValue, destinationValue);
                }
            },
            onSwapOriginDestinationTracking: function(departureValue, destinationValue) {
                var types = {
                    oneWayTrip: 'one_way',
                    roundTrip: 'return',
                    multipleTrip: 'multidestination',
                };
                Odigeo.trigger(this.widget.id + ':swapOriginDestinationClicked',
                    {
                        type: types[this.model.get('type')] || '',
                        departureValue: (Odigeo._.isNull(departureValue) ? 'empty' : 'filled'),
                        destinationValue: (Odigeo._.isNull(destinationValue) ? 'empty' : 'filled'),
                    });
            },
        }),

        widgets: {},

        init: function() {
            this.options = Odigeo.parseOptions(this.options);
            if (!Odigeo._.isEmpty(this.options.airlineFilterIata) && !Odigeo._.isEmpty(this.options.airlineFilterName)) {
                Odigeo.SessionScope.isFromAirlineWMP = true;
                this.view.showAirlineFilter(this.options.airlineFilterName, true);
            }
            // Main binding of the correctness of the model
            this.model.on('change', Odigeo._.bind(function() {
                if (this.model.isValid()) {
                    this.view.onModelValid();
                } else {
                    this.view.onModelInvalid();
                }
            }, this));

            // Binding of other useful functions
            this.listenTo(validator, 'sameCity', Odigeo._.bind(this.view.sameCityHandler, this.view));

            // First validation
            this.model.trigger('change');

            Odigeo.trigger(this.id + ':init');

            // Select the multi-stop tab at the flight searchbox
            if (this.options.isMultiStop) {
                this.setFlightType('multipleTrip');
            }
        },

        setFlightType: function(flightType) {
            this.view.widgets.flightType.trigger('setValue', flightType);
        },

        getFlightType: function() {
            return this.model.get('type');
        },

        setCheckInDates: function(checkInDate, checkOutDate) {
            this.model.set('checkInDate', checkInDate);
            this.model.set('checkOutDate', checkOutDate);
        },

        setCollectionMethod: function(collectionMethodValue) {
            this.model.set('collectionMethod', collectionMethodValue);
        },

        validateCheckInDates: function() {
            return validator.validateCheckinDates(
                this.model.get('checkInDate'),
                this.model.get('checkOutDate'));
        },

        updateAirline: function(addAirlineFilter) {
            var airlines = this.model.get('airlinesCodes');
            if (!addAirlineFilter) {
                var index = airlines.indexOf(this.options.airlineFilterIata);
                if (index !== -1) {
                    airlines.splice(index, 1);
                }
            } else {
                airlines.push(this.options.airlineFilterIata);
            }

            this.model.set('airlinesCodes', airlines);
        },

        hasSelectedInspirationalDestination: function() {
            var searchSegments = this.model.get('searchSegments');
            return Flights.hasAnywhereDestination(searchSegments);
        },

        isFutureDepartureDate(departureDate) {
            const futureDepartureDate = Odigeo.Utils.parseDate(departureDate);
            const today = Odigeo.Utils.getToday();
            return Odigeo.Utils.isFutureDate(futureDepartureDate, today);
        },

        hasToShowFlexibleDates(data) {
            const [firstSegment] = data.searchSegments;
            return tripTypeUtils.isOneWayOrRoundTrip(data.type)
                && this.isFutureDepartureDate(firstSegment.dateStr);
        },

        getDepartureIata: function() {
            return this.model.getRequest().searchSegments[0].departure.iata;
        },

        execute: function(fn) {
            var args = [].slice.call(arguments, 1);

            switch (fn) {
                case 'setFlightType':
                    this.setFlightType.apply(this, args);
                    break;
                case 'getFlightType':
                    this.getFlightType();
                    break;
                case 'setCheckInDates':
                    this.setCheckInDates.apply(this, args);
                    break;
                case 'fillForm':
                    this.view.fillForm.apply(this.view, args);
                    break;
                case 'blurAll':
                    this.view.blurAll.apply(this.view, args);
                    break;
                case 'onChangeAgeSelectors':
                    return this.view.changeAgeSelectors.apply(this.view, args);
                case 'onTapPaxSelectorLabel':
                    return this.view.tapPaxSelectorLabel.apply(this.view, args);
                case 'validateFlightAges':
                    return this.view.validateFlightAges.apply(this.view, args);
                case 'validateAges':
                    return this.view.validateAges.apply(this.view, args);
                case 'updateRoomPassengers':
                    this.view.updateRoomPassengers.apply(this.view, args);
                    break;
                case 'setCollectionMethod':
                    this.setCollectionMethod.call(this, args[0]);
                    break;
                case 'validateCheckInDates':
                    return this.validateCheckInDates(this);
                case 'updateCheckInOutCalendars':
                    this.view.updateCheckInOutCalendars();
                    break;
                case 'hasSelectedInspirationalDestination':
                    return this.hasSelectedInspirationalDestination();
                case 'checkPaxQuickAccess':
                    this.view.checkPaxQuickAccess.apply(this.view, args);
                    break;
                case 'updateAgeSelector':
                    this.view.updateAgeSelector.apply(this.view, args);
                    break;
                case 'changeSearchButtonsState':
                    return this.view.changeSearchButtonsState.apply(this.view, args);
                // added for AB test OFIS-266
                case 'hideSubmitError':
                    this.view.hideSubmitError.apply(this.view, args);
                    break;
                case 'changeAddFlightWrapperVisibility':
                    this.view.changeAddFlightWrapperVisibility.apply(this.view, args);
                    break;
                case 'changeSwapOriginDestinationVisibility':
                    this.view.changeSwapOriginDestinationVisibility.apply(this.view, args);
                    break;
                case 'checkDpButtonAnimation':
                    this.view.checkDpButtonAnimation.apply(this.view, args);
                    break;
            }

            return this;
        },

    }));
